'use client';

import { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronLeft, ChevronRight, Trash2, X, Plus, Pencil, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { HeroLogo, HeroLogoConfig, api } from '@/lib/api';

// Height presets in px
const HEIGHT_PRESETS = [
    { key: 'small', value: 28 },
    { key: 'medium', value: 44 },
    { key: 'large', value: 64 },
] as const;

interface LogoCardProps {
    logo: HeroLogo;
    index: number;
    total: number;
    logoHeight: number;
    onMoveLeft: () => void;
    onMoveRight: () => void;
    onReplace: (file: File) => Promise<void>;
    onDelete: () => Promise<void>;
}

function LogoCard({ logo, index, total, logoHeight, onMoveLeft, onMoveRight, onReplace, onDelete }: LogoCardProps) {
    const t = useTranslations('landing.heroLogos');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleReplace = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try { await onReplace(file); }
        finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
    };

    const handleDelete = async () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            setTimeout(() => setConfirmDelete(false), 3000);
            return;
        }
        await onDelete();
    };

    return (
        <div className="border border-slate-200 flex flex-col w-32">
            {/* Image preview at actual display height */}
            <div className="bg-slate-50 flex items-center justify-center p-3" style={{ height: logoHeight + 24 }}>
                <img src={logo.image_path} alt="" className="max-w-full object-contain" style={{ maxHeight: logoHeight }} />
            </div>

            {/* Controls row */}
            <div className="flex items-center justify-between px-1.5 py-1 border-t border-slate-200 bg-white gap-1">
                {/* Reorder */}
                <div className="flex items-center shrink-0">
                    <button onClick={onMoveLeft} disabled={index === 0}
                        className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-20 cursor-pointer">
                        <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={onMoveRight} disabled={index === total - 1}
                        className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-20 cursor-pointer">
                        <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                </div>

                {/* Replace (pencil) + Delete (trash) */}
                <div className="flex items-center shrink-0">
                    <label className="p-1 text-slate-500 hover:text-slate-800 cursor-pointer transition-colors" title={t('replace')}>
                        {uploading
                            ? <div className="w-3.5 h-3.5 border border-slate-400 border-t-transparent rounded-full animate-spin" />
                            : <Pencil className="w-3.5 h-3.5" />}
                        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleReplace} disabled={uploading} />
                    </label>
                    <button
                        onClick={handleDelete}
                        className={`p-1 cursor-pointer transition-colors ${confirmDelete ? 'text-rose-600 bg-rose-50' : 'text-slate-400 hover:text-rose-600'}`}
                        title={t('deleteLogo')}
                    >
                        <Trash2 className={`w-3.5 h-3.5 ${confirmDelete ? 'animate-pulse' : ''}`} />
                    </button>
                </div>
            </div>
        </div>
    );
}

interface HeroLogoEditorProps {
    logos: HeroLogo[];
    config: HeroLogoConfig;
    onChange: (logos: HeroLogo[]) => void;
    onConfigChange: (config: HeroLogoConfig) => void;
    onClose: () => void;
}

export default function HeroLogoEditor({ logos, config, onChange, onConfigChange, onClose }: HeroLogoEditorProps) {
    const t = useTranslations('landing.heroLogos');
    const [uploading, setUploading] = useState(false);
    const addInputRef = useRef<HTMLInputElement>(null);

    const handleAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const newLogo = await api.uploadLogo(file);
            onChange([...logos, newLogo]);
        } finally {
            setUploading(false);
            if (addInputRef.current) addInputRef.current.value = '';
        }
    };

    const handleReplace = async (logo: HeroLogo, file: File) => {
        const updated = await api.replaceLogo(logo.id, file);
        onChange(logos.map(l => l.id === updated.id ? updated : l));
    };

    const handleDelete = async (logo: HeroLogo) => {
        await api.deleteLogo(logo.id);
        onChange(logos.filter(l => l.id !== logo.id));
    };

    const handleMove = async (index: number, direction: 'left' | 'right') => {
        const next = [...logos];
        const swapIndex = direction === 'left' ? index - 1 : index + 1;
        [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
        const reordered = next.map((l, i) => ({ ...l, order: i }));
        onChange(reordered);
        await api.reorderLogos(reordered.map(l => ({ id: l.id, order: l.order })));
    };

    const updateConfig = async (patch: Partial<HeroLogoConfig>) => {
        const next = { ...config, ...patch };
        onConfigChange(next);
        await api.updateLogoConfig(patch);
    };

    const activePreset = HEIGHT_PRESETS.find(p => p.value === config.logo_height);

    return (
        <section className="bg-white px-6 md:px-10 pt-24 pb-8 border-t border-slate-200">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-slate-900 text-xl font-medium">{t('editLogos')}</h2>
                    <button onClick={onClose} className="flex items-center gap-1.5 px-3 py-1.5 text-slate-500 hover:text-slate-900 text-sm cursor-pointer">
                        <X className="w-4 h-4" />
                        {t('doneEditing')}
                    </button>
                </div>

                {/* Config controls */}
                <div className="flex items-start gap-8 mb-6 pb-6 border-b border-slate-200">
                    {/* Alignment */}
                    <div>
                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-2">{t('alignment')}</label>
                        <div className="flex gap-1">
                            {(['left', 'center', 'right'] as const).map(a => {
                                const Icon = a === 'left' ? AlignLeft : a === 'center' ? AlignCenter : AlignRight;
                                const label = a === 'left' ? t('alignLeft') : a === 'center' ? t('alignCenter') : t('alignRight');
                                return (
                                    <button key={a} onClick={() => updateConfig({ alignment: a })} title={label}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs cursor-pointer transition-colors border ${config.alignment === a ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
                                        <Icon className="w-3.5 h-3.5" />
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Size */}
                    <div>
                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-2">{t('size')}</label>
                        <div className="flex gap-1">
                            {HEIGHT_PRESETS.map(p => (
                                <button key={p.key} onClick={() => updateConfig({ logo_height: p.value })}
                                    className={`px-3 py-1.5 text-xs cursor-pointer transition-colors border ${config.logo_height === p.value ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
                                    {t(`size${p.key.charAt(0).toUpperCase() + p.key.slice(1)}` as any)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {logos.length === 0 && (
                    <p className="text-slate-400 italic text-sm mb-4">{t('noLogos')}</p>
                )}

                <div className="flex flex-wrap gap-3 mb-6">
                    {logos.map((logo, index) => (
                        <LogoCard
                            key={logo.id}
                            logo={logo}
                            index={index}
                            total={logos.length}
                            logoHeight={config.logo_height}
                            onMoveLeft={() => handleMove(index, 'left')}
                            onMoveRight={() => handleMove(index, 'right')}
                            onReplace={(file) => handleReplace(logo, file)}
                            onDelete={() => handleDelete(logo)}
                        />
                    ))}
                </div>

                <label className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm border border-slate-300 cursor-pointer transition-colors w-fit">
                    <Plus className="w-4 h-4" />
                    {uploading ? t('uploading') : t('addLogo')}
                    <input ref={addInputRef} type="file" accept="image/*" className="hidden" onChange={handleAdd} disabled={uploading} />
                </label>
            </div>
        </section>
    );
}
